import { useState, useEffect, useCallback } from 'react';
import { Button, Input } from '@playcastdotio/Shadcn';
import { peerCoordinator } from '../../coordinators/init';

const MIN_BITRATE_MBPS = 0.5;
const MAX_BITRATE_MBPS = 20;
const DEFAULT_DESKTOP_MBPS = 6;
const BPS_PER_MBPS = 1_000_000;

export const BitrateControl = () => {
  const [inputMbps, setInputMbps] = useState<string>(DEFAULT_DESKTOP_MBPS.toString());
  const [activeOverride, setActiveOverride] = useState<number | null>(
    () => peerCoordinator.getBitrateOverride(),
  );
  const [peerCount, setPeerCount] = useState(() => peerCoordinator.getConnectedPeers().length);

  useEffect(() => {
    const interval = setInterval(() => {
      setPeerCount(peerCoordinator.getConnectedPeers().length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleApply = useCallback(() => {
    const mbps = parseFloat(inputMbps);
    if (!Number.isFinite(mbps) || mbps < MIN_BITRATE_MBPS || mbps > MAX_BITRATE_MBPS) return;

    const bps = Math.round(mbps * BPS_PER_MBPS);
    peerCoordinator.setBitrateOverride(bps);
    setActiveOverride(bps);
  }, [inputMbps]);

  const handleReset = useCallback(() => {
    peerCoordinator.setBitrateOverride(null);
    setActiveOverride(null);
    setInputMbps(DEFAULT_DESKTOP_MBPS.toString());
  }, []);

  const activeMbps = activeOverride !== null
    ? (activeOverride / BPS_PER_MBPS).toFixed(1)
    : null;

  return (
    <div className='flex flex-col space-y-4 rounded-lg border border-gray-700 p-4'>
      <h3 className='text-lg font-semibold'>Bitrate Override</h3>

      <div className='flex flex-col space-y-2'>
        <div className='flex items-center space-x-2'>
          <label className='w-24 text-sm font-medium'>Max Bitrate:</label>
          <Input
            type='number'
            value={inputMbps}
            onChange={(e) => setInputMbps(e.target.value)}
            min={MIN_BITRATE_MBPS}
            max={MAX_BITRATE_MBPS}
            step={0.5}
            className='w-24'
          />
          <span className='text-sm text-gray-400'>Mbps</span>
          <Button variant='default' size='sm' onClick={handleApply}>
            Apply
          </Button>
          <Button variant='outline' size='sm' onClick={handleReset}>
            Reset
          </Button>
        </div>

        <div className='text-xs text-gray-500'>
          <p>
            {activeOverride !== null
              ? `Override active: ${activeMbps} Mbps (applied to ${peerCount} peer${peerCount !== 1 ? 's' : ''})`
              : `Using defaults (desktop 6 Mbps / mobile 4 Mbps) — ${peerCount} peer${peerCount !== 1 ? 's' : ''} connected`}
          </p>
          <p>Range: {MIN_BITRATE_MBPS}–{MAX_BITRATE_MBPS} Mbps. Affects current and future peers.</p>
        </div>
      </div>
    </div>
  );
};
